import {Component, computed, effect, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {SearchService} from '../../../../services/search/search.service';
import {RoutesAddonsEnums, RoutesEnums, RoutesNamesEnum} from 'src/app/enums/RoutesEnums';
import {UrlTermsSymbolsEnum} from '../../../../enums/SeoEnums';
import {RelatedLink} from '../../types/RelatedLink';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {Subscription} from 'rxjs';
import {RelatedService} from '../../services/related.service';

@Component({
  selector: 'app-related-category',
  templateUrl: './related-category.component.html',
})
export class RelatedCategoryComponent implements OnInit, OnDestroy {
  public categoryType = signal<RoutesAddonsEnums>(null);
  public categoryDataKey = signal<string>(null);
  public categoryAddon = signal<UrlTermsSymbolsEnum>(null);
  public categoryTypeName = signal<string>(null);
  public h1Title = signal<string>(null);
  public introText = signal<string>(null);
  public pageType: DLPageTypeEnum = DLPageTypeEnum.OTHER_PAGE;
  public breadcrumbs: Breadcrumb[] = [];
  private searchService = inject(SearchService);
  private handledWidgetData = this.searchService.searchWidgetData;
  public categoryItems = computed<RelatedLink[]>(() => {
    if (
      this.handledWidgetData() &&
      this.handledWidgetData().caregiverUnitTypes &&
      this.handledWidgetData().careServiceTypes &&
      this.handledWidgetData().symptoms &&
      this.categoryType() &&
      this.categoryDataKey() &&
      this.categoryAddon()
    ) {
      const links: RelatedLink[] = [];
      this.handledWidgetData()[this.categoryDataKey()].forEach((item) => {
        const itemUrlPathWithAddon: string = `${item.urlPath}-${this.categoryAddon()}`;
        const link: RelatedLink = {
          name: item.name,
          routerLink: ['/', RoutesEnums.SEARCH, itemUrlPathWithAddon],
          title: `Hitta ${item.name}`,
          moreRouterLink: ['/', RoutesEnums.RELATED, this.categoryType(), itemUrlPathWithAddon],
          moreTitle: `Relaterade sökningar på ${item.name}`,
        };
        links.push(link);
      });
      return links;
    }
    return [];
  });
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private relatedService: RelatedService,
  ) {
    effect(() => {
      if (this.categoryType() && this.categoryTypeName() && this.categoryAddon()) {
        this.relatedService.updateMetaTags(this.categoryType(), this.categoryTypeName(), null, this.categoryAddon());
        this.relatedService.trackPageView(this.categoryType(), this.categoryTypeName(), null);
      }
    });
  }

  ngOnInit(): void {
    this.searchService.softResetState();
    this.searchService.resetWidgetState();
    this.onRouteUpdate();
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteUpdate();
        }
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private onRouteUpdate(): void {
    this.categoryType.set(this.route.snapshot.params['categoryType']);
    this.setInitialValuesForCategoryType();
    this.buildBreadcrumbs();
  }

  private setInitialValuesForCategoryType(): void {
    switch (this.categoryType()) {
      case RoutesAddonsEnums.RELATED_CARESERVICE:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_CARESERVICE);
        this.categoryDataKey.set('careServiceTypes');
        this.categoryAddon.set(UrlTermsSymbolsEnum.SERVICE_TYPE);
        this.introText.set('');
        this.h1Title.set(`Relaterade sökningar på ${RoutesNamesEnum.RELATED_CARESERVICE}`);
        break;
      case RoutesAddonsEnums.RELATED_SYMPTOM:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_SYMPTOM);
        this.categoryDataKey.set('symptoms');
        this.categoryAddon.set(UrlTermsSymbolsEnum.SYMPTOM);
        this.introText.set('');
        this.h1Title.set(`Relaterade sökningar på ${RoutesNamesEnum.RELATED_SYMPTOM}`);
        break;
      case RoutesAddonsEnums.RELATED_CAREPROVIDER:
      default:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_CAREPROVIDER);
        this.categoryDataKey.set('caregiverUnitTypes');
        this.categoryAddon.set(UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE);
        this.introText.set(
          'Välkommen till Sveriges största samling av legitimerade vårdgivare! Här kan du jämföra, ' +
            'lista dig och boka tid hos vårdcentraler, tandläkare, psykologer, naprapater, kiropraktorer, hudläkare ' +
            'och ögonläkare. Se omdömen, betyg och hitta de bästa vårdalternativen nära dig. Oavsett vilken vård du ' +
            'söker hjälper vi dig att göra ett informerat val.',
        );
        this.h1Title.set('Jämför och Boka Sveriges Bästa Legitimerade Vårdgivare');
    }
  }

  private buildBreadcrumbs() {
    this.breadcrumbs = [];

    this.breadcrumbs.push({
      routeParams: ['/', RoutesEnums.RELATED, this.categoryType()],
      routeQueryParams: null,
      anchor: this.categoryTypeName(),
    });
  }
}
